// <DefaultPromoSwiper> -- THE renderer of a default promotional composition,
// used by swiperPub1500x400.js on shops without their own promotional
// swiper AND by the Swiper Manager previews / editor, so what Family sees is
// exactly what visitors get.
//
// Frame: a fixed aspect ratio (the configuration's, 7:4 like the historical
// shop images by default; optional mobile ratio), so the height is known
// before any image loads and never jumps between slides. Its real width is
// measured (ResizeObserver) and drives every text size.
//
// Swiper options: page instance (swiperOptions prop) > this configuration >
// Swiper Settings default for "promoBanner" > component origin -- through
// the same useElakaySwiper() as every managed swiper. Only one image is
// loaded eagerly (the first); the others wait until it has loaded.
import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import useElakaySwiper, { useSwiperDevice } from "../slider/swiperSettings/useElakaySwiper";
import { elakayAutoText } from "../pageInside/dataElakay";
import DefaultPromoSlide from "./DefaultPromoSlide";
import "./DefaultPromo.css";

const text = elakayAutoText;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const ANIMATIONS_DISABLED_CLASS = "elakay-animations-disabled";

const useReducedMotion = () => {
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  const [value, setValue] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const update = () => setValue(media.matches);
    if (media.addEventListener) media.addEventListener("change", update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", update);
      else media.removeListener(update);
    };
  }, []);
  return value;
};

// Elakay's "animations disabled" platform mode forces transition:none on
// every element: Swiper would freeze after its first move, so slides change
// instantly there (same handling as DynamicSwiper).
const useAnimationsDisabled = () => {
  const read = () => typeof document !== "undefined" && document.documentElement.classList.contains(ANIMATIONS_DISABLED_CLASS);
  const [value, setValue] = useState(read);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => setValue(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return value;
};

const ESTIMATED_WIDTH = { mobile: 375, tablet: 768, desktop: 1280 };

const useFrameSize = (ref, ratio, device, mounted) => {
  const estimate = () => {
    const width = typeof window !== "undefined" ? Math.min(window.innerWidth || 1280, 1920) : ESTIMATED_WIDTH[device] || 1280;
    return { width, height: width / ratio };
  };
  const [size, setSize] = useState(estimate);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => {
      const width = node.clientWidth;
      if (!width) return;
      const height = node.clientHeight || width / ratio;
      setSize((prev) => (Math.abs(prev.width - width) < 0.5 && Math.abs(prev.height - height) < 0.5 ? prev : { width, height }));
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, ratio, mounted]);
  return size;
};

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

// Per device, per group: the page's own options win over the configuration.
const mergeSwiperOptions = (configOptions, pageOptions) => {
  const out = {};
  ["desktop", "tablet", "mobile"].forEach((device) => {
    const layers = [configOptions?.[device], pageOptions?.[device]].filter(isPlainObject);
    if (!layers.length) return;
    const merged = {};
    layers.forEach((layer) => Object.entries(layer).forEach(([group, values]) => {
      if (isPlainObject(values)) merged[group] = { ...(merged[group] || {}), ...values };
    }));
    out[device] = merged;
  });
  return out;
};

const DefaultPromoSwiper = ({
  data, shopName = "", swiperOptions, eager = false, device: forcedDevice, className = "", label,
  activeIndex, onActiveIndexChange, selectedTextId, onSelectText, replayToken = 0, frameRef: externalFrameRef,
}) => {
  const viewportDevice = useSwiperDevice();
  const device = forcedDevice || viewportDevice;
  const reducedMotion = useReducedMotion();
  const animationsDisabled = useAnimationsDisabled();
  const instant = reducedMotion || animationsDisabled;
  const slides = useMemo(() => (Array.isArray(data?.slides) ? data.slides : []), [data?.slides]);
  const frameDef = device === "mobile" && data?.frame?.mobile ? data.frame.mobile : data?.frame;
  const ratio = (Number(frameDef?.width) || 1200) / (Number(frameDef?.height) || 686);
  const innerRef = useRef(null);
  const frameRef = externalFrameRef || innerRef;
  const frame = useFrameSize(frameRef, ratio, device, slides.length > 0);
  const instanceOptions = useMemo(() => mergeSwiperOptions(data?.swiperOptions, swiperOptions), [data?.swiperOptions, swiperOptions]);
  const managed = useElakaySwiper("promoBanner", {
    swiperOptions: instanceOptions,
    slideCount: slides.length,
    autoNavigation: true,
    deviceOverride: forcedDevice,
  });
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [instance, setInstance] = useState(null);

  // Editor: follow the slide being edited.
  useEffect(() => {
    if (!instance || instance.destroyed || activeIndex === undefined || activeIndex === null) return;
    const target = Math.max(0, Math.min(slides.length - 1, activeIndex));
    if (instance.realIndex !== target) instance.slideToLoop(target, instant ? 0 : 300);
  }, [instance, activeIndex, slides.length, instant]);

  if (!slides.length) return null;
  const regionLabel = label || text("h_dpr_region_label", "Promotions");
  const compact = frame.width < 480;
  const rootClass = `dpromo${instant ? " dpromo-instant" : ""}${compact ? " is-compact" : ""}${className ? ` ${className}` : ""}`;
  const frameStyle = { aspectRatio: `${frameDef?.width || 1200} / ${frameDef?.height || 686}` };
  const renderSlide = (slide, index) => (
    <DefaultPromoSlide
      key={`${slide.id}-${replayToken}`}
      slide={slide}
      frame={frame}
      shopName={shopName}
      active={index === visibleIndex}
      animate={!instant}
      priority={index === visibleIndex && index === 0 && eager ? "high" : "normal"}
      selectedTextId={selectedTextId}
      onSelectText={onSelectText}
    />
  );

  if (slides.length === 1) {
    return (
      <section className={rootClass} aria-label={regionLabel}>
        <div className="dpromo-frame" ref={frameRef} style={frameStyle}>{renderSlide(slides[0], 0)}</div>
      </section>
    );
  }

  const autoplay = managed.swiperProps.autoplay && !reducedMotion && !onSelectText ? managed.swiperProps.autoplay : false;
  return (
    <section className={rootClass} aria-label={regionLabel} aria-roledescription="carousel">
      <div className="dpromo-frame" ref={frameRef} style={frameStyle}>
        <Swiper
          key={`${managed.key}-${Boolean(autoplay)}-${instant}-${slides.length}`}
          {...managed.swiperProps}
          // Specific class (never the generic "mySwiper": other sliders of
          // Layout define their own height for it).
          className={`dpromo-swiper ${managed.className}`}
          style={{ ...managed.style, height: "100%" }}
          speed={instant ? 0 : managed.swiperProps.speed}
          autoplay={autoplay}
          initialSlide={activeIndex ? Math.min(slides.length - 1, activeIndex) : 0}
          onSwiper={setInstance}
          onSlideChange={(current) => {
            setVisibleIndex(current.realIndex);
            if (onActiveIndexChange) onActiveIndexChange(current.realIndex);
          }}
          a11y={{
            enabled: true,
            prevSlideMessage: text("h_dpr_prev_slide", "Previous slide"),
            nextSlideMessage: text("h_dpr_next_slide", "Next slide"),
            paginationBulletMessage: text("h_dpr_go_to_slide", "Go to slide {{index}}"),
          }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.id || index}>{renderSlide(slide, index)}</SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default memo(DefaultPromoSwiper);
